import { useLang, useT } from "../i18n";
import { breadcrumbLd, useSeo, webPageLd } from "../lib/seo";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export type LegalPageKey = "legal" | "privacy" | "terms";

/** /legal, /privacy and /terms: the same layout, the text comes from t.legalPages. */
/** Section text: paragraphs, "## " sub-headings, and lines starting with "- " grouped into a bulleted list */
function SectionText({ text }: { text: string }) {
  const blocks: (string | string[] | { heading: string })[] = [];
  for (const line of text.split("\n")) {
    if (line.startsWith("## ")) blocks.push({ heading: line.slice(3) });
    else if (line.startsWith("- ")) {
      const last = blocks[blocks.length - 1];
      if (Array.isArray(last)) last.push(line.slice(2));
      else blocks.push([line.slice(2)]);
    } else if (line.trim()) blocks.push(line);
  }
  return (
    <div className="mt-2 grid gap-2 text-[13px] leading-relaxed text-ink-soft">
      {blocks.map((b, i) =>
        typeof b === "object" && "heading" in b ? (
          <h3 key={i} className="m-0 mt-2 text-[13px] font-semibold text-ink">
            {b.heading}
          </h3>
        ) : Array.isArray(b) ? (
          <ul key={i} className="m-0 grid list-disc gap-1 pl-5">
            {b.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i} className="m-0">
            {b}
          </p>
        ),
      )}
    </div>
  );
}

export default function LegalPage({ page }: { page: LegalPageKey }) {
  const t = useT();
  const copy = t.legalPages;
  const data = copy[page];
  const { title, sections } = data;
  // Final texts (terms) have their own date and introduction, and no prototype banner
  const final = "final" in data && data.final;
  const updated = "updated" in data ? data.updated : copy.lastUpdated;
  const intro = "intro" in data ? data.intro : [];
  const lang = useLang();
  const seo = t.seo[page];
  useSeo({
    title: seo.title,
    description: seo.description,
    path: `/${page}`,
    jsonLd: [webPageLd("WebPage", seo.title, seo.description, `/${page}`, lang), breadcrumbLd([[t.seo.breadcrumbHome, "/"], [title]])],
  });

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />

      <main className="mx-auto w-full max-w-300 flex-1 px-4 pt-12 pb-24 sm:px-6">
        <h1 className="m-0 text-2xl sm:text-3xl font-medium tracking-[-0.01em] sm:text-[40px]">{title}</h1>
        <p className="mt-2 mb-0 text-[13px] text-muted">{updated}</p>
        {!final && (
          <p className="mt-6 mb-8 rounded-card border border-dashed border-line-strong px-4 py-3 text-xs leading-normal text-muted">
            {copy.placeholder}
          </p>
        )}
        {intro.length > 0 && (
          <div className="mt-6 mb-8 grid gap-2.5 text-sm leading-relaxed text-ink-soft">
            {intro.map((p) => (
              <p key={p} className="m-0">
                {p}
              </p>
            ))}
          </div>
        )}

        {sections.map((section, i) => (
          <section key={section.title} className="border-t border-line-strong py-5">
            <h2 className="m-0 text-base font-medium">
              <span className="mr-2.5 font-mono text-xs text-brand">{String(i + 1).padStart(2, "0")}</span>
              {section.title}
            </h2>
            <SectionText text={section.text} />
          </section>
        ))}
      </main>

      <Footer />
    </div>
  );
}
